import type { LatLng, Layer, Map } from "leaflet";
import { type RefObject, use, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { FixtureSelectionContext } from "@/components/features/map/fixture-selection-provider";
import type { DraggableLayer } from "@/types/map-types";

export interface MovePoint {
    id: string;
    point: LatLng;
}

interface DraggableProps {
    /**
     * Array of layer refs to enable dragging on. When one layer is dragged,
     * the remaining refs become sibling sub-layers that move with it.
     */
    layerRefs: RefObject<Layer | null>[];
    /**
     * Unique identifier for this fixture in the selection context.
     */
    uniqueId: string;
    /**
     * Whether this fixture is currently selected.
     */
    isSelected: boolean;
    /**
     * Callback invoked with the new positions of all moved fixtures after a
     * drag. Must be referentially stable (e.g. wrapped in `useCallback`).
     */
    onMoveEnd: (points: MovePoint[]) => void;
    /**
     * Optional callback invoked when a drag begins. Must be referentially
     * stable (e.g. wrapped in `useCallback`).
     */
    onDragStart?: (map: Map) => void;
    /**
     * Optional callback invoked when a drag ends. Must be referentially
     * stable (e.g. wrapped in `useCallback`).
     */
    onDragEnd?: (map: Map) => void;
    /**
     * Whether the component has mounted and refs are available.
     */
    isMounted: boolean;
}

/**
 * Shared hook that enables Leaflet-path-drag on an array of layer refs.
 *
 * Each layer in `layerRefs` becomes independently draggable. When one is
 * dragged, the other refs plus all other selected fixture layers (from
 * the selection context) move together as sub-layers.
 *
 * @param {DraggableProps} options - Configuration for the draggable behavior.
 */
export const useDraggable = ({
    layerRefs,
    uniqueId,
    isSelected,
    onMoveEnd,
    onDragStart,
    onDragEnd,
    isMounted,
}: DraggableProps) => {
    const selection = use(FixtureSelectionContext);

    // Read selectedLights lazily during dragstart to discover other selected
    // fixtures' layers. Keeping it in a ref avoids tearing down and
    // re-registering Leaflet handlers every time the Redux selection array
    // gets a new identity.
    const selectedIds = useSelector(
        ({ map }) => map.general.selectedLights as string[],
    );

    const selectedIdsRef = useRef(selectedIds);

    useEffect(() => {
        selectedIdsRef.current = selectedIds;
    }, [selectedIds]);

    const getSiblingLayers = useCallback(() => {
        const layers: DraggableLayer[] = [];

        for (const id of selectedIdsRef.current) {
            if (id === uniqueId) continue;
            const entry = selection?.get(id);
            if (!entry) continue;

            for (const layer of entry.layers) {
                if (layer) layers.push(layer as unknown as DraggableLayer);
            }
        }

        return layers;
    }, [uniqueId, selection]);

    const toggleDragging = useCallback(
        (layer: DraggableLayer, siblings: DraggableLayer[]) => {
            if (!isSelected) {
                layer.dragging.disable();
                return () => {};
            }

            layer.dragging.enable();

            const handleDragStart = () => {
                layer._map.movingLights = true;
                layer.subLayers = [...siblings, ...getSiblingLayers()];
                onDragStart?.(layer._map);
            };

            const handleDragEnd = () => {
                if (!layer._map) return;

                onDragEnd?.(layer._map);

                const matrix = layer.dragging._matrix;
                const points: MovePoint[] = [];

                if (layer.options.id) {
                    points.push({
                        id: layer.options.id,
                        point: layer.getBounds().getCenter(),
                    });
                }

                for (const sub of layer.subLayers) {
                    sub.dragging._transformPoints(matrix);
                    sub._updatePath();
                    sub._project();
                    sub._transform(null);

                    if (sub.options.id) {
                        points.push({
                            id: sub.options.id,
                            point: sub.getBounds().getCenter(),
                        });
                    }
                }

                if (points.length > 0) {
                    onMoveEnd(points);
                }

                layer._map.moved = true;
                layer._map._renderer._redraw();

                const map = layer._map;

                requestAnimationFrame(() => {
                    if (!map) return;
                    map.moved = false;
                    map.movingLights = false;
                });
            };

            layer.on("dragstart", handleDragStart);
            layer.on("dragend", handleDragEnd);

            return () => {
                layer.off("dragstart", handleDragStart);
                layer.off("dragend", handleDragEnd);
            };
        },
        [isSelected, onDragStart, onDragEnd, onMoveEnd, getSiblingLayers],
    );

    useEffect(() => {
        if (!isMounted) return;

        const activeLayers = layerRefs
            .map((ref) => ref.current)
            .filter(Boolean) as unknown as DraggableLayer[];

        if (activeLayers.length === 0) return;

        const cleanups: (() => void)[] = [];

        for (let i = 0; i < activeLayers.length; i++) {
            const siblings = activeLayers.filter((_, j) => j !== i);
            cleanups.push(toggleDragging(activeLayers[i], siblings));
        }

        return () => {
            for (const cleanup of cleanups) cleanup();
        };
    }, [toggleDragging, isMounted, layerRefs]);
};
